import React, { useEffect, useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart2, Settings, Home, ChevronLeft, ChevronRight, LogOut, Menu, X, TrendingUp, Brain, Megaphone,
  Bell, Search, HelpCircle, ChevronDown, ChevronUp, User, CreditCard, Users, Calendar, AlertTriangle, MessageCircle, Shield, DollarSign, CheckSquare, Check, Mail, FileText, HeartPulse, Timer, Phone, ScrollText, LogIn
} from 'lucide-react';
import { useAuth, useSessionTracking } from '../context/AuthContext';
import { GetLiveProvider } from './get-live/GetLiveContext';
import A2PTrialBanner from './get-live/A2PTrialBanner';
import GetLiveCompanion from './get-live/GetLiveCompanion';
import ReviewerIntroVideo from './ReviewerIntroVideo';
import HowToVideoModal from './get-live/HowToVideoModal';
import { Crosshair as HunterNavIcon } from 'lucide-react';
import MessengerIcon from './MessengerIcon';
import supabase from '../lib/supabaseClient';
import NotificationCenter from './NotificationCenter';
import NotificationsModal from './NotificationsModal';
import apiClient from '../lib/apiClient';
import PlatformUpdatesModal from './PlatformUpdatesModal';
import { useA2PStatusPolling } from '../components/useA2PStatusPolling';
import GlobalSearchPalette from './GlobalSearchPalette';
import SurfWidget from './surf/SurfWidget';

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, canAccessEnterprise, role, refreshUser } = useAuth();

  // Add session tracking here
  useSessionTracking();
  
  // A2P Status Polling
const handleA2PStatusChange = (change) => {
  console.log('📢 A2P Status Change:', change);
  
  // Add notification
  const notification = {
    id: Date.now(),
    message: change.message,
    type: getNotificationType(change.newStatus),
    timestamp: new Date()
  };
  
  setA2pNotifications(prev => [notification, ...prev]);
  
  // Auto-remove after 10 seconds
  setTimeout(() => {
    setA2pNotifications(prev => prev.filter(n => n.id !== notification.id));
  }, 10000);
};

const getNotificationType = (status) => {
  if (['APPROVED', 'VERIFIED', 'approved', 'verified'].includes(status)) {
    return 'success';
  }
  if (['FAILED', 'rejected'].includes(status)) {
    return 'error';
  }
  return 'info';
};

useA2PStatusPolling(user, handleA2PStatusChange);

  // ── Web chat unread badge ───────────────────────────────────────────────────
  // Mirrors the Hunter badge: a persisted 60s poll of a DB count (NOT a realtime-only
  // counter). Counts distinct web-chat conversations that have a real inbound visitor
  // message since the tenant last opened the inbox, so the badge backfills on load and
  // survives a refresh — the old realtime-only version started at 0 every reload and
  // only caught messages while the app was open, which is why live chats were missed.
  // The "last viewed" marker is persisted per-tenant in platform_settings (no migration).
  useEffect(() => {
    if (!user?.tenant_id) return;
    // On the inbox itself the badge is always 0 (and the clear effect persists the marker).
    if (location.pathname === '/web-chat') { setWebChatUnread(0); return; }
    const fetchWebChatUnread = async () => {
      try {
        const { data: lv } = await supabase
          .from('platform_settings')
          .select('value')
          .eq('tenant_id', user.tenant_id)
          .eq('key', 'web_chat_last_viewed_at')
          .maybeSingle();
        const since = lv?.value || '1970-01-01T00:00:00Z';
        const { data: rows } = await supabase
          .from('messages')
          .select('session_id')
          .eq('tenant_id', user.tenant_id)
          .eq('channel', 'web')
          .eq('direction', 'inbound')
          .neq('message_body', '__page_open__')
          .gt('timestamp', since)
          .limit(500);
        const conversations = new Set((rows || []).map(r => r.session_id)).size;
        setWebChatUnread(conversations);
      } catch (err) {
        console.error('web chat unread count failed:', err);
      }
    };
    fetchWebChatUnread();
    const interval = setInterval(fetchWebChatUnread, 60000);
    return () => clearInterval(interval);
  }, [user?.tenant_id, location.pathname]);

  // Clear badge + persist the "last viewed" marker when opening the inbox.
  useEffect(() => {
    if (location.pathname !== '/web-chat' || !user?.tenant_id) return;
    setWebChatUnread(0);
    supabase
      .from('platform_settings')
      .upsert(
        { tenant_id: user.tenant_id, key: 'web_chat_last_viewed_at', value: new Date().toISOString(), updated_at: new Date().toISOString() },
        { onConflict: 'key,tenant_id', ignoreDuplicates: false }
      )
      .then(({ error }) => { if (error) console.error('web_chat_last_viewed_at upsert failed:', error); });
  }, [location.pathname, user?.tenant_id]);

  // Hide the Web Chat nav item unless this tenant actually has a campaign wired to
  // the web widget (tenants.widget_campaign_id). Without an assigned campaign the
  // inbox is always empty, so showing it just confuses customers who don't use web chat.
  useEffect(() => {
    if (!user?.tenant_id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('tenants')
          .select('widget_campaign_id')
          .eq('id', user.tenant_id)
          .maybeSingle();
        if (!cancelled) setHasWebChatCampaign(!!data?.widget_campaign_id);
      } catch (err) {
        console.error('web chat campaign check failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.tenant_id]);

  // Messenger nav item: same rule as Web Chat, keyed on tenants.messenger_campaign_id (the one
  // Messenger binding). Its own query on purpose, so a problem reading this column can never
  // take the Web Chat item down with it.
  useEffect(() => {
    if (!user?.tenant_id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('tenants')
          .select('messenger_campaign_id')
          .eq('id', user.tenant_id)
          .maybeSingle();
        if (error) throw error;
        if (!cancelled) setHasMessengerCampaign(!!data?.messenger_campaign_id);
      } catch (err) {
        console.error('messenger campaign check failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.tenant_id]);

  // Same pattern as Web Chat above: hide the Voice nav item unless voice is actually
  // in use right now. TWO WRONG GATES TRIED LIVE 2026-09-13, in order: (1)
  // tenants.voice_ai_campaign_id alone missed calls from an ordinary SMS campaign
  // calling back its own imported number; (2) voice_ai_enabled alone over-corrected
  // — it's a one-time provisioning flag that never turns back off, so the tab stayed
  // visible even after every campaign was switched back to SMS. The real answer is
  // neither flag alone: voice-provisioned AND at least one campaign actually
  // configured for voice right now — either the dedicated cold-caller line, or any
  // Follow-up step set to Voice on any campaign.
  useEffect(() => {
    if (!user?.tenant_id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: tenant } = await supabase
          .from('tenants')
          .select('voice_ai_enabled, voice_ai_campaign_id')
          .eq('id', user.tenant_id)
          .maybeSingle();
        if (!tenant?.voice_ai_enabled) {
          if (!cancelled) setHasVoiceCampaign(false);
          return;
        }
        if (tenant.voice_ai_campaign_id) {
          if (!cancelled) setHasVoiceCampaign(true);
          return;
        }
        const { data: voiceFollowupCampaigns } = await supabase
          .from('campaigns')
          .select('id')
          .eq('tenant_id', user.tenant_id)
          .or('path1_followup_channel.eq.voice,followup_channel_1.eq.voice,followup_channel_2.eq.voice,followup_channel_3.eq.voice')
          .limit(1);
        if (!cancelled) setHasVoiceCampaign((voiceFollowupCampaigns || []).length > 0);
      } catch (err) {
        console.error('voice campaign check failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.tenant_id]);

  // Show terms modal for existing users who haven't agreed yet.
  // user.terms_agreed_at is:
  //   - undefined  → profile hasn't been fetched from DB yet (fast-path tempUser)
  //   - null       → DB confirmed no agreement exists
  //   - ISO string → user has agreed
  // Only show the modal when the DB has explicitly confirmed null.
  useEffect(() => {
    if (!user) return;
    // Skip if profile hasn't loaded terms_agreed_at from DB yet (still undefined)
    if (user.terms_agreed_at === undefined) return;
    // Review-sandbox accounts never see the Terms popup (server-side exemption: _shared/authUtils.ts).
    if (user.terms_agreed_at === null && user.is_reviewer !== true) {
      setShowTermsModal(true);
    } else {
      setShowTermsModal(false);
    }
  }, [user?.terms_agreed_at, user?.is_reviewer]);

  // A one-time pledge, not a per-update re-consent — stamped once in
  // agree-terms and never reset by send-tos-alert's re-arm. A returning user
  // who already agreed doesn't need to recheck it on every future Terms
  // update, only the Terms checkbox itself (which DOES reset, since that's
  // tied to whatever actually changed).
  const alreadyAgreedResponsibleUse = !!user?.responsible_use_agreed_at;

  const handleAcceptTerms = async () => {
    if (!termsAgreed || !(alreadyAgreedResponsibleUse || tcpaAgreed)) return;
    setTermsSaving(true);
    try {
      const { error } = await supabase.functions.invoke('agree-terms', {});
      if (error) {
        console.error('agree-terms error:', error);
        alert('Something went wrong. Please try again.');
      } else {
        // Refresh user state so terms_agreed_at is populated and the useEffect closes the modal
        await refreshUser();
        setShowTermsModal(false);
      }
    } catch (err) {
      console.error('Failed to record terms agreement:', err);
      alert('Something went wrong. Please try again.');
    }
    setTermsSaving(false);
  };

  const isControlRoom = location.pathname === '/control-room';
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [collapsed, setCollapsed] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showTopUserMenu, setShowTopUserMenu] = useState(false);
  const [isGlobalSearchOpen, setIsGlobalSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({
    name: 'SurFox',
    industry: 'sales',
    loading: true
  });
  const [showUpdatesModal, setShowUpdatesModal] = useState(false);
  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [a2pNotifications, setA2pNotifications] = useState([]);
  const [chatLoaded, setChatLoaded] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [webChatUnread, setWebChatUnread] = useState(0);
  const [hasWebChatCampaign, setHasWebChatCampaign] = useState(false);
  const [hasMessengerCampaign, setHasMessengerCampaign] = useState(false);
  const [hasVoiceCampaign, setHasVoiceCampaign] = useState(false);
  const [escalationCount, setEscalationCount] = useState(0);
  const [hunterCount, setHunterCount] = useState(0);

  // Fetch open escalation count — global_admin only (cross-tenant inbox).
  // Tenants file via the LeadDetail Escalate button but never see the queue.
  useEffect(() => {
    if (role !== 'global_admin') return;
    const fetchCount = async () => {
      const { count } = await supabase
        .from('escalations')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'open');
      setEscalationCount(count || 0);
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => clearInterval(interval);
  }, [role, location.pathname]);

  // Unreviewed Hunter signals — global_admin only (SurFox-internal prospecting).
  useEffect(() => {
    if (role !== 'global_admin') return;
    const fetchHunter = async () => {
      const { count } = await supabase
        .from('hunter_signals')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'unreviewed');
      setHunterCount(count || 0);
    };
    fetchHunter();
    const interval = setInterval(fetchHunter, 60000);
    return () => clearInterval(interval);
  }, [role, location.pathname]);

  // Paused subscription gate
  const showPausedModal = user?.subscription_status === 'paused';

  // Terms agreement modal state
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [tcpaAgreed, setTcpaAgreed] = useState(false);
  const [termsSaving, setTermsSaving] = useState(false);

 // Clean up notification message for display:
// - strips "Lead: Name (phone)(status)" prefix
// - strips score text like "Score: 30/100"
// - extracts just the reason/body text
const cleanNotificationMessage = (message) => {
  if (!message) return message;
  let cleaned = message
    // Strip score patterns
    .replace(/[\s\-–—]*[\(\[]?\s*[Ss]core:?\s*\d+\s*\/\s*\d+\s*[\)\]]?\.?/g, '')
    .replace(/[\s\-–—]*\d+\/100\.?/g, '')
    // Strip "Lead: Name (phone)(anything) " prefix
    .replace(/^Lead:\s[^)]+\)\s*/i, '')
    // Strip remaining leading "(status) " like "(Lukewarm) "
    .replace(/^\([^)]*\)\s*/g, '')
    // Strip "Reason: " label — the text is the reason, label is redundant
    .replace(/^Reason:\s*/i, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
  return cleaned || message; // fall back to original if over-stripped
};

// Keep old name as alias for any other callers
const stripScoreFromMessage = cleanNotificationMessage;

// ─── SMS usage banner ────────────────────────────────────────────────────────
// Mirrors the tiers in supabase/functions/check-sms-usage-alerts (75/90/100), so
// the in-app surface and the email tell the same story. Usage is bursty rather
// than linear — a single bulk-launch day can move a tenant ~28 points — so the
// early tier is the one that actually buys time to top up before outreach freezes.
//
// The 75% tier is dismissible for the session: it is advisory, and a permanent
// bar across every page for the last quarter of an allotment is nagging. 90% and
// 100% are not dismissible; by then the interruption is imminent or already real.
// Dismissal is keyed by tier, so crossing into a higher tier re-shows the banner.
const USAGE_TIERS = [
  {
    at: 100,
    dismissible: false,
    wrap: 'border-red-300 bg-red-50',
    icon: 'text-red-600',
    head: 'text-red-900',
    body: 'text-red-800',
    cta: 'bg-red-600 hover:bg-red-700',
    title: 'Monthly message limit reached - outreach is paused',
    text: 'New outreach and follow-ups are paused until your cycle resets. SurFox is still replying to leads who message you. Add messages to resume sending right away.',
    action: 'Add messages'
  },
  {
    at: 90,
    dismissible: false,
    wrap: 'border-amber-300 bg-amber-50',
    icon: 'text-amber-600',
    head: 'text-amber-900',
    body: 'text-amber-800',
    cta: 'bg-amber-600 hover:bg-amber-700',
    title: "You're close to your monthly message limit",
    text: 'At 100% new outreach and follow-ups pause automatically. Replies to existing leads keep going.',
    action: 'Add messages'
  },
  {
    at: 75,
    dismissible: true,
    wrap: 'border-sky-300 bg-sky-50',
    icon: 'text-sky-600',
    head: 'text-sky-900',
    body: 'text-sky-800',
    cta: 'bg-sky-600 hover:bg-sky-700',
    title: "You've used most of this cycle's messages",
    text: 'Outreach pauses automatically at 100%. If you have a bulk campaign planned, topping up now keeps it from stopping partway through.',
    action: 'Add messages'
  }
];

// ─── Per-tenant admin notice ─────────────────────────────────────────────────
// A hand-written message aimed at ONE tenant, stored on tenants.admin_banner as
// { key, title, body }. Null on every other tenant, so nobody else sees it.
//
// This is display-only on purpose. The messaging kill switch (messaging_enabled)
// looks like a per-tenant flag we could piggyback on, but it is a send gate read
// by _shared/messagingGate.ts — setting it to talk to someone would stop their
// outbound messaging.
//
// Dismissal is PER PERSON, not per browser: the dismissed `key` is appended to
// users_profile.dismissed_banners, so clicking the X once retires the notice on
// every device that person logs in from. localStorage is kept alongside it purely
// as a fast local echo — it makes the X feel instant and covers the case where
// the write fails — but the DB row is the source of truth.
//
// The banner deliberately does not render until that lookup resolves. Rendering
// first and hiding on response would flash an already-dismissed notice at someone
// on a second device, which is exactly the experience this is meant to prevent.
// If the lookup errors we show the banner: a notice seen twice beats one nobody
// ever sees.
//
// This lives in the component rather than AuthContext on purpose. AuthContext has
// two load paths and the common one (auth metadata present) never reads
// users_profile at all, so hanging the flag on `user` would leave it undefined for
// most logins.
//
// Changing `key` in the DB re-shows the notice to everyone in that tenant.
// Review sandbox (tenants.is_reviewer): an outside evaluator's locked-down account. Always visible so anyone
// looking at a screenshot knows the data is sample data. Also turns the server's "read-only" refusals (Express
// REVIEWER_READ_ONLY, edge functions, and Postgres 42501 from direct writes) into one friendly toast.
// Axios refusals arrive as a 'reviewer-blocked' event from lib/apiClient.js; fetch (edge functions and
// supabase-js) is watched here, only while a reviewer is signed in.
function ReviewerSandboxBanner({ user }) {
  const [blocked, setBlocked] = useState(false);
  const [blockedMsg, setBlockedMsg] = useState(''); // a specific message from the button that was pressed; empty = the generic one
  const isReviewer = user?.is_reviewer === true;

  useEffect(() => {
    if (!isReviewer) return undefined;
    let timer;
    const show = (e) => {
      const msg = e && e.detail && typeof e.detail.message === 'string' ? e.detail.message : '';
      setBlockedMsg(msg);
      setBlocked(true);
      clearTimeout(timer);
      timer = setTimeout(() => setBlocked(false), msg ? 6500 : 4000);
    };
    window.addEventListener('reviewer-blocked', show);
    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      const res = await originalFetch(...args);
      if (res.status === 403) {
        res.clone().json()
          // Edge functions return only { error } (no code), so also match the sandbox wording.
          .then((j) => { if (j && (j.code === 'REVIEWER_READ_ONLY' || j.code === '42501' || (typeof j.error === 'string' && j.error.startsWith('This is a review sandbox')))) show(); })
          .catch(() => {});
      }
      return res;
    };
    return () => {
      window.fetch = originalFetch;
      window.removeEventListener('reviewer-blocked', show);
      clearTimeout(timer);
    };
  }, [isReviewer]);

  if (!isReviewer) return null;
  const isDemo = user.is_prospect_demo === true;
  const expires = user.reviewer_expires_at
    ? new Date(user.reviewer_expires_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
    : null;
  return (
    <>
      <div className="mb-4 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold text-indigo-900">{isDemo ? 'Your SurFox AI demo: built from your website' : 'Review sandbox: everything here is sample data'}</p>
          <p className="text-indigo-800 mt-0.5">
            {isDemo
              ? 'The two campaigns, one for fresh leads and one for cold lists you upload, were written from your own website. The leads and conversations are examples written for your business, so you can see what a live account looks like. Messaging is turned off and nothing is ever sent, so click anywhere you like. The Test conversation on the campaign and Surf both work for real.'
              : 'Onboarding is already done for this account, so there is nothing to set up. The leads, conversations and results are made up for this demo. Messaging is turned off and changes are disabled, so click anywhere you like. The Test conversation on each campaign and Surf both work for real.'}
            {expires ? ` Your access runs until ${expires}.` : ''}
          </p>
        </div>
      </div>
      {blocked && (
        <div
          className={`fixed left-0 right-0 z-[10002] flex justify-center px-4 pointer-events-none ${isDemo ? 'bottom-[calc(42vh+1rem)] lg:bottom-6 lg:right-[396px]' : 'bottom-6'}`}
          role="status"
        >
          <div className="max-w-md text-center rounded-lg bg-slate-900 text-white text-sm px-4 py-3 shadow-lg">
            {blockedMsg || (isDemo ? 'This is a demo, so that action is disabled.' : 'This is a review sandbox, so that action is disabled.')}
          </div>
        </div>
      )}
    </>
  );
}

function AdminNoticeBanner({ user }) {
  const banner = user?.admin_banner || null;
  const bannerKey = banner?.key || null;
  const userId = user?.id || null;
  const storageKey = bannerKey ? `sfx_admin_banner_dismissed_${bannerKey}` : null;
  const [dismissed, setDismissed] = useState(false);
  const [resolved, setResolved] = useState(false);

  // Re-runs when the banner arrives or its key changes — `user` is filled in by
  // two async AuthContext paths, so the first render usually has no banner yet.
  useEffect(() => {
    let cancelled = false;
    if (!bannerKey) { setResolved(false); return; }

    // Local echo first: if this browser already recorded the dismissal there is
    // nothing to wait for.
    let localHit = false;
    try { localHit = localStorage.getItem(storageKey) === '1'; } catch { /* private mode */ }
    if (localHit) { setDismissed(true); setResolved(true); return; }

    if (!userId) return; // user not hydrated yet; effect re-runs when it is

    (async () => {
      const { data, error } = await supabase
        .from('users_profile')
        .select('dismissed_banners')
        .eq('id', userId)
        .single();

      if (cancelled) return;
      if (error) {
        // Show it rather than swallow it, and say why in the console so a silent
        // "banner never appeared" report has something to go on.
        console.warn('AdminNoticeBanner: could not read dismissed_banners:', error.message);
        setDismissed(false);
        setResolved(true);
        return;
      }
      const list = Array.isArray(data?.dismissed_banners) ? data.dismissed_banners : [];
      const already = list.includes(bannerKey);
      if (already) {
        // Backfill the local echo so later loads in this browser skip the query.
        try { localStorage.setItem(storageKey, '1'); } catch { /* private mode */ }
      }
      setDismissed(already);
      setResolved(true);
    })();

    return () => { cancelled = true; };
  }, [bannerKey, storageKey, userId]);

  if (!banner || !banner.body || dismissed || !resolved) return null;

  const dismiss = async () => {
    setDismissed(true); // optimistic: the X must feel instant
    try { localStorage.setItem(storageKey, '1'); } catch { /* private mode */ }
    if (!userId) return;

    // Read-modify-write on a per-user array. Safe here because the only writer is
    // this person in this tab; a lost update would at worst re-show the notice.
    const { data, error: readErr } = await supabase
      .from('users_profile')
      .select('dismissed_banners')
      .eq('id', userId)
      .single();
    if (readErr) {
      console.warn('AdminNoticeBanner: could not read dismissed_banners before write:', readErr.message);
      return; // localStorage still covers this browser
    }
    const list = Array.isArray(data?.dismissed_banners) ? data.dismissed_banners : [];
    if (list.includes(bannerKey)) return;

    const { error: writeErr } = await supabase
      .from('users_profile')
      .update({ dismissed_banners: [...list, bannerKey] })
      .eq('id', userId);
    if (writeErr) console.warn('AdminNoticeBanner: could not persist dismissal:', writeErr.message);
  };

  return (
    <div className="mb-4 rounded-xl border border-teal-200 bg-teal-50 px-4 py-3.5 flex items-start gap-3">
      <div className="w-7 h-7 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Check className="w-4 h-4 text-teal-700" />
      </div>
      <div className="text-sm min-w-0 flex-1">
        {banner.title && (
          <p className="font-semibold text-teal-900">{banner.title}</p>
        )}
        {/* Blank lines in the stored text become paragraphs, so the notice can be
            written as prose in SQL without embedding markup. */}
        {String(banner.body).split(/\n\s*\n/).map((para, i) => (
          <p key={i} className="text-teal-800 mt-1 leading-relaxed">{para.trim()}</p>
        ))}
      </div>
      <button
        onClick={dismiss}
        aria-label="Dismiss notice"
        className="flex-shrink-0 p-1 rounded-lg text-teal-600 hover:text-teal-900 hover:bg-teal-100 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function UsageLimitBanner({ user, pathname }) {
  const pct = user?.sms_usage_pct;
  const tier = pct == null ? null : USAGE_TIERS.find(t => pct >= t.at) || null;
  const [dismissedTier, setDismissedTier] = useState(() => {
    try { return Number(sessionStorage.getItem('sfx_usage_banner_dismissed')) || null; }
    catch { return null; }
  });

  if (!user || !tier) return null;
  // Messaging kill switch already explains why nothing is sending; a usage banner
  // on top of it would be noise pointing at the wrong cause.
  if (user.messaging_enabled === false) return null;
  // No point nagging them toward Billing while they are standing on it.
  if (pathname?.startsWith('/billing')) return null;
  if (tier.dismissible && dismissedTier === tier.at) return null;

  const dismiss = () => {
    setDismissedTier(tier.at);
    try { sessionStorage.setItem('sfx_usage_banner_dismissed', String(tier.at)); } catch { /* private mode */ }
  };

  const remaining = user.sms_limit_total == null
    ? null
    : Math.max(user.sms_limit_total - (user.sms_count || 0), 0);

  return (
    <div className={`mb-4 rounded-xl border px-4 py-3 flex items-start gap-3 ${tier.wrap}`}>
      <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${tier.icon}`} />
      <div className="text-sm flex-1 min-w-0">
        <p className={`font-semibold ${tier.head}`}>{tier.title}</p>
        <p className={`mt-0.5 ${tier.body}`}>
          {tier.at < 100 && remaining != null && (
            <span className="font-medium">{remaining.toLocaleString()} messages left this cycle. </span>
          )}
          {tier.text}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Link
          to="/billing"
          className={`px-3 py-1.5 rounded-lg text-white text-xs font-semibold transition-colors ${tier.cta}`}
        >
          {tier.action}
        </Link>
        {tier.dismissible && (
          <button
            onClick={dismiss}
            className={`px-2 py-1.5 text-xs font-medium ${tier.body} hover:opacity-70`}
          >
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Lead inventory banner ───────────────────────────────────────────────────
// The usage banner answers "are we allowed to send". This one answers "is there
// anyone left to send to", a second and unrelated way for a tenant to go quiet
// that had no surface anywhere. The Da Silva Team ran their list to zero on
// 2026-08-19 and nothing in the product said so.
//
// Counted with the SAME predicate the sender uses (supabase/functions/send-batch:
// ai_sent=false + queued_for_processing=false on an ai_on + is_active campaign)
// MINUS the leads that can never receive a message. That subtraction is the whole
// point: Da Silva's raw pool read 51 while 29 were undialable numbers and 22 were
// stranded on dead queue jobs, so the honest reachable count was 0. A banner
// counting unsendable rows would have said "51 left" on the day they ran dry.
//
// Fails silent. No live campaign, a query error, or a not-yet-loaded count all
// render nothing: a parked tenant should not carry a permanent bar, and a false
// "you are out of leads" is worse than no banner at all.
const LEAD_POOL_LOW = 100;

// Last measured pool, kept OUTSIDE the component and keyed by tenant. The
// notification poller re-renders Layout every 30s (see below), and any remount
// would otherwise restart this at "unknown", blanking the banner for the length
// of a round-trip and bouncing the page twice as it left and re-entered the
// layout. Seeding from the cache means a remount paints the last known count
// immediately and the refetch only ever corrects it.
const leadPoolCache = new Map();
// The most recent count from ANY tenant, used only while tenant_id is missing.
// Measured 2026-08-20: on each 30s notification poll this component is torn out
// of <main> and rebuilt with user.tenant_id briefly undefined, so a lookup keyed
// by tenant misses, the banner renders null, and it does not return until the
// refetch lands 279ms later. That gap is the flicker, and it moved the page
// twice per poll. Falling back to the last known value paints immediately.
let lastLeadPool = null;
// sessionStorage, not module scope. Measured 2026-08-20: on the blank render the
// module-level cache read back EMPTY, so the module is being re-evaluated and any
// in-memory memo is wiped with it. sessionStorage outlives that (and a reload), is
// synchronous, so the very first render after a rebuild already has a number and
// there is no gap to see.
const poolKey = (t) => `sfx_lead_pool:${t}`;
const readPool = (t) => {
  if (!t) return null;
  try {
    const v = sessionStorage.getItem(poolKey(t));
    return v == null || v === "" ? null : Number(v);
  } catch { return null; }
};
const writePool = (t, n) => {
  if (!t) return;
  try { sessionStorage.setItem(poolKey(t), String(n)); } catch { /* private mode */ }
};

// Last time we actually ASKED the database, per tenant.
//
// The effect below re-runs far more often than its `[tenantId]` dependency should
// allow. Measured live 2026-08-27 with a PerformanceObserver in the browser: it fires
// in PAIRS about 130ms apart on a 30s and 60s cadence (the notification and unread
// pollers), and across all open tabs that reached ~46 requests/minute. Critically,
// all 67 observed requests carried the SAME tenant_id — so every repeat was asking a
// question whose answer could not have changed. The remount that re-triggers it is
// still not root-caused; this makes the refire rate stop mattering instead of
// pretending to have fixed it. The query, its guards and its .error checks are
// unchanged, and `shown` below still paints the cached number on every render, so
// there is no visible difference.
//
// sessionStorage, NOT a ref and NOT module scope. A ref dies with the remount this is
// defending against. Module scope looked sufficient, but the pool cache immediately
// above was moved out of module scope for exactly this reason — 2026-08-20 measured it
// reading back empty — and if that is real then a module-level timestamp is wiped too
// and this throttle silently does nothing. sessionStorage survives both, and survives a
// reload, which module scope does not. Per tab, which is the right grain: each tab
// throttles itself to one lookup per window.
const POOL_TTL_MS = 5 * 60 * 1000;
const poolAtKey = (t) => `sfx_lead_pool_at:${t}`;
const readPoolAt = (t) => {
  if (!t) return 0;
  try { return Number(sessionStorage.getItem(poolAtKey(t))) || 0; } catch { return 0; }
};
const writePoolAt = (t, ms) => {
  if (!t) return;
  try { sessionStorage.setItem(poolAtKey(t), String(ms)); } catch { /* private mode */ }
};

function LeadInventoryBanner({ user, pathname }) {
  const tenantId = user?.tenant_id;
  const [pool, setPool] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('sfx_lead_pool_dismissed') === '1'; }
    catch { return false; }
  });

  useEffect(() => {
    if (!tenantId) return undefined;

    // Asked recently enough. The count is "how many leads are left to contact",
    // which moves on the timescale of an outreach run, not a render. The cached
    // value still renders via `shown` below, so skipping the fetch is invisible.
    // Stamped BEFORE the request, not after: a failing lookup must also wait out
    // the window rather than retrying on every remount, since a retry storm
    // against a slow query is what made this expensive in the first place.
    const lastFetchedAt = readPoolAt(tenantId);
    if (lastFetchedAt && Date.now() - lastFetchedAt < POOL_TTL_MS) return undefined;
    writePoolAt(tenantId, Date.now());

    let cancelled = false;

    (async () => {
      const { data: campaigns, error: campaignError } = await supabase
        .from('campaigns')
        .select('id')
        .eq('tenant_id', tenantId)
        .eq('ai_on', true)
        .eq('is_active', true);

      // A bad column does not throw here: PostgREST returns data:null with the
      // reason in .error, which reads exactly like "no live campaigns". Check it
      // before trusting the emptiness.
      if (campaignError) {
        console.warn('LeadInventoryBanner: campaign lookup failed:', campaignError.message);
        return;
      }
      if (!campaigns || campaigns.length === 0) return;

      const { count, error: leadError } = await supabase
        .from('leads')
        .select('id', { count: 'exact', head: true })
        .in('campaign_id', campaigns.map(c => c.id))
        .eq('ai_sent', false)
        .eq('queued_for_processing', false)
        .not('sms_undeliverable', 'is', true)
        .not('sms_opted_out', 'is', true);

      if (leadError) {
        console.warn('LeadInventoryBanner: reachable-lead count failed:', leadError.message);
        return;
      }
      leadPoolCache.set(tenantId, count ?? 0);
      lastLeadPool = count ?? 0;
      writePool(tenantId, count ?? 0);
      if (!cancelled) setPool(count ?? 0);
    })();

    return () => { cancelled = true; };
  }, [tenantId]);

  // A known tenant uses its own count; only a missing tenant_id falls back to the
  // last one seen, and either is corrected the moment the refetch resolves.
  const shown = pool ?? readPool(tenantId) ?? (tenantId ? leadPoolCache.get(tenantId) ?? null : lastLeadPool);

  if (shown == null || shown > LEAD_POOL_LOW) return null;
  // The same two suppressions the usage banner uses: the kill switch already
  // explains the silence, and there is no point nagging them toward the page
  // they are already standing on.
  if (user?.messaging_enabled === false) return null;
  if (pathname?.startsWith('/campaign-management')) return null;

  const empty = shown === 0;
  if (!empty && dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem('sfx_lead_pool_dismissed', '1'); } catch { /* private mode */ }
  };

  return (
    <div className={`mb-4 rounded-xl border px-4 py-3 flex items-start gap-3 ${empty ? 'border-amber-300 bg-amber-50' : 'border-sky-300 bg-sky-50'}`}>
      <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${empty ? 'text-amber-600' : 'text-sky-600'}`} />
      <div className="text-sm flex-1 min-w-0">
        <p className={`font-semibold ${empty ? 'text-amber-900' : 'text-sky-900'}`}>
          {empty ? 'No leads left to contact' : "You're almost out of leads to contact"}
        </p>
        <p className={`mt-0.5 ${empty ? 'text-amber-800' : 'text-sky-800'}`}>
          <span className="font-medium">
            {shown.toLocaleString()} {shown === 1 ? 'lead' : 'leads'} left to reach on your active campaigns.{' '}
          </span>
          {empty
            ? 'Everyone on them has been contacted. SurFox keeps replying and following up with existing leads, but no new outreach can go out until you add more. Adding messages will not restart it.'
            : 'Add more before the list runs out to keep outreach going.'}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Link
          to="/campaign-management"
          className={`px-3 py-1.5 rounded-lg text-white text-xs font-semibold transition-colors ${empty ? 'bg-amber-600 hover:bg-amber-700' : 'bg-sky-600 hover:bg-sky-700'}`}
        >
          Add leads
        </Link>
        {!empty && (
          <button onClick={dismiss} className="px-2 py-1.5 text-xs font-medium text-sky-800 hover:opacity-70">
            Dismiss
          </button>
        )}
      </div>
    </div>
  );
}


 // Smart notification organization for enterprise scale
const organizeNotifications = (rawNotifications) => {
  if (!rawNotifications || rawNotifications.length === 0) {
    return { individual: [], batched: [] };
  }

  // Separate critical from regular notifications
  const critical = rawNotifications.filter(n => 
    n.priority === 'critical'
  );
  
  const regular = rawNotifications.filter(n => 
    n.priority !== 'critical'
  );

  // Show max 5 critical notifications individually
  const individual = critical.slice(0, 5);
  
  // Batch the rest
  const batched = [];
  
  // Add remaining critical alerts as batch if needed
  if (critical.length > 5) {
    batched.push({
      id: 'critical-batch',
      type: 'batch',
      title: `${critical.length - 5} More Critical Alerts`,
      message: `${critical.slice(5, 8).map(n => n.leads?.name || 'Unknown').join(', ')}${critical.length > 8 ? ` and ${critical.length - 8} others` : ''}`,
      priority: 'critical',
      count: critical.length - 5,
      created_at: critical[5]?.created_at
    });
  }
  
  // Add regular notifications as batch if any exist
  if (regular.length > 0) {
    batched.push({
      id: 'regular-batch',
      type: 'batch', 
      title: `${regular.length} New Hot Lead${regular.length === 1 ? '' : 's'}`,
      message: `${regular.slice(0, 3).map(n => n.leads?.name || 'Unknown').join(', ')}${regular.length > 3 ? ` and ${regular.length - 3} others ready for contact` : ' ready for contact'}`,
      priority: 'medium',
      count: regular.length,
      created_at: regular[0]?.created_at
    });
  }

  return { individual, batched };
};

  // Fetch company information
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      if (!user?.tenant_id) {
        console.log('❌ No tenant ID available for company fetch');
        return;
      }

      console.log('🔍 Fetching company info for tenant:', user.tenant_id);

      try {
        // Column-scoped on purpose: `select('*')` pulled the ENTIRE tenants row into the
        // browser, including the plaintext Twilio auth tokens, on every shell mount.
        // Do not widen it back to '*'.
        // NOTE: `business_type` is read below but is NOT a column on tenants, so it must
        // stay OUT of this select — naming it makes PostgREST 400 the whole query (42703)
        // and the company name disappears. Under select('*') it was simply undefined, so
        // that branch has always been dead and the behaviour here is unchanged.
        const { data: tenant, error } = await supabase
          .from('tenants')
          .select('name, industry')
          .eq('id', user.tenant_id)
          .single();

        if (error) {
          console.error('❌ Company fetch error:', error);
        }

        if (error) {
          console.error('Error fetching company info:', error);
          setCompanyInfo({
            name: 'Surfox',
            industry: 'real estate',
            loading: false
          });
          return;
        }

        if (tenant) {
          // Extract company name and determine industry
          let companyName = tenant.name || 'Surfox';
          
          console.log('🏷️ Original company name:', companyName);
          
          // Clean up the company name if it has the default format
          if (companyName.includes("'s Company")) {
            companyName = companyName.replace("'s Company", "");
          }

          // Determine industry/business type
          let industry = 'business';
          if (tenant.industry) {
            industry = tenant.industry.toLowerCase();
          } else if (tenant.business_type) {
            industry = tenant.business_type.toLowerCase();
          } else if (user.email) {
            // Try to guess from email domain or company name
            const domain = user.email.split('@')[1]?.toLowerCase();
            console.log('🌐 Email domain:', domain);
            console.log('🏢 Company name lower:', companyName.toLowerCase());
            
            if (domain?.includes('real') || companyName.toLowerCase().includes('real') || 
                companyName.toLowerCase().includes('rei') || companyName.toLowerCase().includes('property') ||
                companyName.toLowerCase().includes('dream') || companyName.toLowerCase().includes('homes') ||
                companyName.toLowerCase().includes('estate')) {
              industry = 'real estate';
            } else if (domain?.includes('staff') || companyName.toLowerCase().includes('staff') || 
                       companyName.toLowerCase().includes('recruit') || companyName.toLowerCase().includes('hire') ||
                       companyName.toLowerCase().includes('talent') || companyName.toLowerCase().includes('employment')) {
              industry = 'staffing';
            } else if (domain?.includes('tech') || companyName.toLowerCase().includes('tech') || 
                       companyName.toLowerCase().includes('software') || companyName.toLowerCase().includes('app') ||
                       companyName.toLowerCase().includes('digital') || companyName.toLowerCase().includes('dev')) {
              industry = 'technology';
            } else if (companyName.toLowerCase().includes('consult') || companyName.toLowerCase().includes('service') ||
                       companyName.toLowerCase().includes('solution') || companyName.toLowerCase().includes('group')) {
              industry = 'consulting';
            }
          }

          console.log('✅ Final company info:', { name: companyName, industry });

          setCompanyInfo({
            name: companyName,
            industry: industry,
            loading: false
          });
        }
      } catch (error) {
        console.error('Error fetching company info:', error);
        setCompanyInfo({
          name: 'Surfox',
          industry: 'real estate',
          loading: false
        });
      }
    };

    fetchCompanyInfo();
  }, [user?.tenant_id]);

  // Global search keyboard shortcut - Cmd+/ or Ctrl+/
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '/') {
        e.preventDefault();
        setIsGlobalSearchOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Fetch notifications based on user role - FIXED: No WebSocket connections
  useEffect(() => {
    if (!user?.id) return;
    // Allow global_admin to proceed even without tenant_id
    if (!user?.tenant_id && role !== 'global_admin') return;

    const fetchNotifications = async () => {
      try {
        setNotificationsLoading(true);
        
        let query = supabase
          .from('notifications')
          .select(`
            id,
            lead_id,
            title,
            message,
            priority,
            read,
            created_at,
            data,
            tenant_id,
            leads (
              id,
              name,
              phone,
              campaign_id,
              tenant_id,
              campaigns!campaign_id (
                id,
                name,
                assigned_to_sales_team_id,
                tenant_id
              )
            )
          `)
          .eq('type', 'lead_escalation')
          .order('created_at', { ascending: false })
          .limit(20);

        // If NOT global_admin, filter by tenant_id
        if (role !== 'global_admin') {
          query = query.eq('tenant_id', user.tenant_id);
          console.log('🔒 Filtering notifications by tenant_id:', user.tenant_id);
        } else {
          console.log('🌍 Global admin - fetching notifications from ALL tenants');
        }

        // If user is not business_admin or global_admin, filter by assigned campaigns
        if (!['business_admin', 'global_admin', 'partner_admin'].includes(role)) {
          // Get campaigns assigned to this user
          let campaignsQuery = supabase
            .from('campaigns')
            .select('id')
            .eq('assigned_to_sales_team_id', user.id);

          // Filter campaigns by tenant if not global_admin
          if (role !== 'global_admin') {
            campaignsQuery = campaignsQuery.eq('tenant_id', user.tenant_id);
          }

          const { data: userCampaigns } = await campaignsQuery;

          const campaignIds = userCampaigns?.map(c => c.id) || [];

          if (campaignIds.length > 0) {
            // Get lead IDs for these campaigns (can't filter on nested fields directly)
            const { data: campaignLeads } = await supabase
              .from('leads')
              .select('id')
              .in('campaign_id', campaignIds);

            const leadIds = campaignLeads?.map(l => l.id) || [];

            if (leadIds.length > 0) {
              query = query.in('lead_id', leadIds);
            } else {
              // No leads in assigned campaigns, show no notifications
              setNotifications([]);
              setUnreadCount(0);
              setNotificationsLoading(false);
              return;
            }
          } else {
            // User has no assigned campaigns, show no notifications
            setNotifications([]);
            setUnreadCount(0);
            setNotificationsLoading(false);
            return;
          }
        }

        const { data, error } = await query;

        if (error) {
          console.error('Error fetching notifications:', error);
          return;
        }

        setNotifications(data || []);
        setUnreadCount(data?.filter(n => !n.read).length || 0);
      } catch (error) {
        console.error('Error in notification fetch:', error);
      } finally {
        setNotificationsLoading(false);
      }
    };

   

    // Initial fetch
    fetchNotifications();

    // 🔥 FIXED: Use polling instead of real-time WebSocket subscriptions
    // Poll for new notifications every 30 seconds
    const pollInterval = setInterval(() => {
      fetchNotifications();
    }, 30000); // 30 seconds

    // Cleanup function
    return () => {
      clearInterval(pollInterval);
    };
  }, [user?.id, user?.tenant_id, role]);

  // Crisp Chat Widget - Lazy Load on User Click
  // This prevents the local network discovery permission prompt on mobile
  const loadCrispChat = useCallback(() => {
    if (chatLoaded || chatLoading || !user?.email) return;

    setChatLoading(true);

    // Initialize Crisp
    window.$crisp = [];
    window.CRISP_WEBSITE_ID = "850ecb8b-77d9-492e-bfc2-265894dc2402";

    // Set user context for better support
    window.$crisp.push(["set", "user:email", user.email]);
    window.$crisp.push(["set", "user:nickname", user.email.split('@')[0]]);
    window.$crisp.push(["set", "session:data", {
      company: companyInfo.name,
      role: role,
      tenant_id: user.tenant_id,
      industry: companyInfo.industry
    }]);

    // Load Crisp script
    const script = document.createElement("script");
    script.src = "https://client.crisp.chat/l.js";
    script.async = true;
    script.onload = () => {
      setChatLoaded(true);
      setChatLoading(false);
      // Open the chat widget after loading
      if (window.$crisp) {
        window.$crisp.push(["do", "chat:open"]);
      }
      console.log('✅ Crisp chat widget loaded and opened for:', user.email);
    };
    script.onerror = () => {
      setChatLoading(false);
      console.error('❌ Failed to load Crisp chat widget');
    };
    document.head.appendChild(script);
  }, [user?.email, companyInfo.name, role, chatLoaded, chatLoading, user?.tenant_id, companyInfo.industry]);

  // Handle chat button click
  const handleChatClick = useCallback(() => {
    if (chatLoaded && window.$crisp) {
      // Chat already loaded, just open it
      window.$crisp.push(["do", "chat:open"]);
    } else {
      // Load chat for the first time
      loadCrispChat();
    }
  }, [chatLoaded, loadCrispChat]);

  // Mark notification as read (routes through server to bypass RLS)
  const markAsRead = async (notificationId) => {
    try {
      await apiClient.patch('/notifications/mark-read', { ids: [notificationId] });
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    navigate(`/lead/${notification.lead_id}`);
    setShowNotifications(false);
  };

  // Delete/dismiss notification (routes through server to bypass RLS)
  const dismissNotification = async (e, notificationId) => {
    e.stopPropagation();

    const target = notifications.find(n => n.id === notificationId);

    try {
      await apiClient.delete(`/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (target && !target.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error dismissing notification:', error);
    }
  };

  // Mark all as read (routes through server to bypass RLS)
  const markAllAsRead = async () => {
    try {
      const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
      if (unreadIds.length === 0) return;

      await apiClient.patch('/notifications/mark-read', { ids: unreadIds });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Add click outside handler to close menus
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTopUserMenu || showUserMenu) {
        const isClickInside = event.target.closest('.user-menu-container');
        if (!isClickInside) {
          setShowTopUserMenu(false);
          setShowUserMenu(false);
        }
      }
      
      // Close search results when clicking outside
      if (showSearchResults) {
        const isClickInsideSearch = event.target.closest('.search-container');
        if (!isClickInsideSearch) {
          setShowSearchResults(false);
        }
      }

      // Close notifications when clicking outside
      if (showNotifications) {
        const isClickInsideNotifications = event.target.closest('.notifications-container');
        if (!isClickInsideNotifications) {
          setShowNotifications(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTopUserMenu, showUserMenu, showSearchResults, showNotifications]);

  // Get page-specific messaging based on current route
  const getPageMessages = () => {
    const companyName = companyInfo.name;
    
    switch (location.pathname) {
      case '/control-room':
        return {
          title: companyInfo.industry === 'staffing' ? 'Candidate Pipeline Control Room' :
                 companyInfo.industry === 'real estate' ? 'Lead Pipeline Control Room' :
                 'SurFox Control Room',
          subtitle: companyInfo.industry === 'staffing' ? 'Monitor and optimize your candidate pipeline' :
                   companyInfo.industry === 'real estate' ? 'Monitor and optimize your lead pipeline' :
                   'Monitor and optimize your business pipeline'
                   
        };
        
      case '/dashboard':
        return {
          title: 'Dashboard',
          subtitle: companyInfo.industry === 'staffing' ? 'Overview of your recruitment metrics and performance' :
                   companyInfo.industry === 'real estate' ? 'Overview of your real estate business performance' :
                   'Overview of your business performance and key metrics'
        };
      case '/business-analytics':
        return {
          title: 'AI Strategy Hub',
          subtitle: companyInfo.industry === 'staffing' ? 'Deep insights into your recruitment and staffing data' :
                   companyInfo.industry === 'real estate' ? 'Deep insights into your real estate business data' :
                   'Deep insights into your business data and trends'
        };
      case '/enterprise-analytics':
        return {
          title: 'Enterprise Analytics',
          subtitle: 'Enterprise-level analytics and cross-business insights'
        };
      case '/campaign-management':
        return {
          title: 'Campaign Management',
          subtitle: companyInfo.industry === 'staffing' ? 'Create and manage recruitment campaigns' :
                   companyInfo.industry === 'real estate' ? 'Create and manage lead generation campaigns' :
                   'Create and manage marketing campaigns'
        };
      case '/escalations':
        return {
          title: 'Escalations',
          subtitle: 'Issues reported by tenants'
        };
     
      case '/settings':
        return {
          title: 'Settings',
          subtitle: 'Configure your SurFox platform and AI automation'
        };
      case '/calendar-settings':
    return {
        title: 'Availability',
        subtitle: 'Configure when your AI can book meetings'
    };
      default:
        return {
          title: location.pathname.slice(1).split('-').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1)
          ).join(' ') || 'Home',
          subtitle: companyInfo.industry === 'staffing' ? 'Manage your staffing and recruitment operations' :
                   companyInfo.industry === 'real estate' ? 'Manage your real estate business' :
                   'Manage your business operations'
        };
    }
  };

  const getPageTitle = () => {
  switch (location.pathname) {
    case '/control-room': return 'Pulse';
    case '/dashboard': return 'Leads';
    case '/web-chat': return 'Web Chat';
    case '/messenger': return 'Messenger';
    case '/voice-calls': return 'Voice';
    case '/ai-studio': return 'AI Studio';
    case '/business-analytics': return 'Strategy';
    case '/enterprise-analytics': return 'Enterprise';
    case '/campaign-management': return 'Campaigns';
    case '/escalations': return 'Escalations';
    case '/settings': return 'Settings';
    case '/calendar-settings': return 'Availability';
    case '/billing': return 'Billing';
    case '/profile': return 'Profile';
    case '/help': return 'Help';
    case '/security-audit': return 'Security Audit';
    case '/admin/affiliate-audit': return 'Affiliate Sign-Ins';
    case '/admin/a2p': return 'A2P Registration Status';
    case '/admin/financials': return 'Financial Intelligence';
    case '/admin/financials/explorer': return 'Cost Explorer';
    case '/admin/financials/unit-economics': return 'Unit Economics';
    case '/admin/financials/invoices': return 'Invoice Management';
    case '/admin/financials/rates': return 'Rate Management';
    case '/admin/email-manager': return 'Email Manager';
    case '/admin/tos-alerts': return 'TOS Alerts';
    case '/admin/voice-trunk-provisioning': return 'Voice Trunk Provisioning';
    case '/admin/reviewers': return 'Review Accounts';
    case '/qc-queue': return 'QC Queue';
    case '/monthly-reports': return 'Monthly Reports';
    default:
      if (location.pathname.startsWith('/admin/financials/customers/')) return 'Customer Costs';
      if (location.pathname.startsWith('/lead/')) return 'Lead Details';
      if (location.pathname.startsWith('/campaign/')) return 'Campaign';
      return '';
  }
};

const messages = getPageMessages();

// Dynamic navigation items based on user role
const getNavItems = () => {
  const baseItems = [
    { path: '/control-room', label: 'Pulse', icon: BarChart2, tourClass: 'tour-controlroom' },
    { path: '/dashboard', label: 'Leads', icon: Home, tourClass: 'tour-dashboard' },
    // Web Chat only shows once a campaign is assigned to the widget (or for global_admin support access).
    ...((hasWebChatCampaign || role === 'global_admin')
      ? [{ path: '/web-chat', label: 'Web Chat', icon: MessageCircle, tourClass: 'tour-webchat' }]
      : []),
    // Messenger sits directly under Web Chat; same visibility rule (campaign bound, or global_admin).
    ...((hasMessengerCampaign || role === 'global_admin')
      ? [{ path: '/messenger', label: 'Messenger', icon: MessengerIcon, tourClass: 'tour-messenger' }]
      : []),
    // Same rule as Web Chat: Voice only shows once a Voice campaign is designated
    // (tenants.voice_ai_campaign_id) — no campaign means no calls could ever land
    // anywhere, so the list would always be empty.
    ...((hasVoiceCampaign || role === 'global_admin')
      ? [{ path: '/voice-calls', label: 'Voice', icon: Phone, tourClass: 'tour-voice' }]
      : []),
    { path: '/ai-studio', label: 'AI Studio', icon: Brain, tourClass: 'tour-aistudio' },
];

    // Add analytics based on role
    const analyticsItems = [];
    
    // Enterprise Analytics - only for global_admin (partner_admin disabled until data is wired up)
    if (role === 'global_admin') {
      analyticsItems.push({
        path: '/enterprise-analytics', 
        label: 'Enterprise Analytics', 
        icon: TrendingUp,
        tourClass: 'tour-enterprise'
      });
    }

    // Campaign Management - for all users now
    analyticsItems.push({
    path: '/campaign-management',
    label: 'Campaigns',
    icon: Megaphone,
    tourClass: 'tour-campaigns'
});

    if (role === 'global_admin') {
      analyticsItems.push({
        path: '/escalations',
        label: 'Escalations',
        icon: AlertTriangle,
        tourClass: 'tour-escalations'
      });
    }

const endItems = [];

    return [...baseItems, ...analyticsItems, ...endItems];
  };

  const navItems = getNavItems();

  const handleLogout = async () => {
  try {
    console.log('🚪 Logging out...');
    
    // Close sessions FIRST, before anything else
    if (user?.id) {
      console.log('🔚 Closing sessions for user:', user.id);
      await supabase
        .from('user_sessions')
        .update({
          session_end: new Date().toISOString(),
          last_activity: new Date().toISOString()
        })
        .eq('user_id', user.id)
        .is('session_end', null);
      
      console.log('✅ Sessions closed');
    }
    
    // Clear storage
    localStorage.removeItem('auth_token');
    sessionStorage.removeItem('auth_token');
    
    // Sign out from Supabase
    const { error } = await supabase.auth.signOut();
    
    if (error) {
      console.error('❌ Logout error:', error);
    }
    
    console.log('✅ Logout complete');
    navigate('/login');
  } catch (err) {
    console.error('❌ Logout exception:', err);
    navigate('/login');
  }
};

  // Get company initials for logo
  const getCompanyInitials = () => {
    if (companyInfo.loading) return 'R';
    
    const name = companyInfo.name;
    if (name === 'Surfox') return 'R';
    
    // Get initials from company name
    const words = name.split(' ').filter(word => word.length > 0);
    if (words.length >= 2) {
      return words[0][0].toUpperCase() + words[1][0].toUpperCase();
    } else if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    return 'BC'; // Business CRM fallback
  };

  // Search functionality
  const performSearch = async (query) => {
    if (!query.trim() || query.length < 2) {
      setSearchResults(null);
      setShowSearchResults(false);
      return;
    }

    console.log('🔍 Searching for:', query);
    console.log('👤 User role:', role);
    console.log('🏢 Tenant ID:', user?.tenant_id);
    setIsSearching(true);
    setShowSearchResults(true);

    try {
      // Build leads query
      // phone_digits stores stripped numeric digits so partial phone search works
      // regardless of how the phone was stored (E.164, formatted, etc.)
      const normalizedPhone = query.replace(/\D/g, '');
      const phoneFilter = normalizedPhone.length >= 3
        ? `,phone_digits.ilike.%${normalizedPhone}%`
        : '';

      let leadsQuery = supabase
        .from('leads_decrypted')
        .select('id, name, email, phone, status, ai_status, ai_score, tenant_id')
        .or(`name.ilike.%${query}%,email.ilike.%${query}%${phoneFilter}`);
      
      // If NOT global_admin, filter by tenant_id
      if (role !== 'global_admin') {
        leadsQuery = leadsQuery.eq('tenant_id', user.tenant_id);
        console.log('🔒 Filtering leads by tenant_id:', user.tenant_id);
      } else {
        console.log('🌍 Global admin - searching ALL tenants for leads');
      }
      
      const { data: leads, error: leadsError } = await leadsQuery.limit(5);

      console.log('📊 Leads results:', leads);
      console.log('❌ Leads error:', leadsError);

      // Build campaigns query
      let campaignsQuery = supabase
        .from('campaigns')
        .select('id, name, description, is_active, ai_on, start_date, end_date, tenant_id')
        .or(`name.ilike.%${query}%,description.ilike.%${query}%`)
        .eq('archived', false); // Don't show archived campaigns
      
      // If NOT global_admin, filter by tenant_id
      if (role !== 'global_admin') {
        campaignsQuery = campaignsQuery.eq('tenant_id', user.tenant_id);
        console.log('🔒 Filtering campaigns by tenant_id:', user.tenant_id);
      } else {
        console.log('🌍 Global admin - searching ALL tenants for campaigns');
      }
      
      const { data: campaigns, error: campaignsError } = await campaignsQuery.limit(5);

      console.log('📊 Campaigns results:', campaigns);
      console.log('❌ Campaigns error:', campaignsError);

      if (leadsError) console.error('Error searching leads:', leadsError);
      if (campaignsError) console.error('Error searching campaigns:', campaignsError);

      setSearchResults({
        leads: leads || [],
        campaigns: campaigns || []
      });
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults(null);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      performSearch(searchQuery);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const handleSearchResultClick = (type, item) => {
    // Navigate to the appropriate page based on the result type
    if (type === 'lead') {
      navigate(`/lead/${item.id}`);  // Changed from /leads/ to /lead/
    } else if (type === 'campaign') {
      navigate(`/campaign/${item.id}`);  // Changed from /campaigns/ to /campaign/
    }
    
    // Clear search
    setSearchQuery('');
    setShowSearchResults(false);
    setSearchResults(null);
  };

  const companyInitials = getCompanyInitials();

  return (
    <div className="flex min-h-screen bg-gray-50 overflow-x-hidden max-w-full">

      {/* Blocking Terms Agreement Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-8">
            <h2 className="text-2xl font-bold text-white mb-3">We've Updated Our Terms of Service</h2>
            <p className="text-white/70 text-sm mb-4">
              {user?.terms_notice_message || "We've made important updates to our Terms of Service. Please review and agree to continue using SurFox AI."}
            </p>
            <p className="mb-6">
              <a
                href="https://www.getsurfox.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-400 hover:underline text-sm"
              >
                Read the full Terms at getsurfox.com/terms
              </a>
            </p>

            <div className="space-y-3 mb-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={termsAgreed}
                  onChange={(e) => setTermsAgreed(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-white/20 accent-blue-400"
                />
                <span className="text-sm text-white/70">
                  I have read and agree to the SurFox AI{' '}
                  <a href="https://www.getsurfox.com/terms" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Terms of Service</a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Privacy Policy</a>.
                </span>
              </label>

              {!alreadyAgreedResponsibleUse && (
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={tcpaAgreed}
                    onChange={(e) => setTcpaAgreed(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-white/20 accent-blue-400"
                  />
                  <span className="text-sm text-white/70">
                    I agree to use SurFox AI responsibly and in compliance with applicable messaging laws, including obtaining proper consent from my leads.
                  </span>
                </label>
              )}
            </div>

            <button
              onClick={handleAcceptTerms}
              disabled={!termsAgreed || !(alreadyAgreedResponsibleUse || tcpaAgreed) || termsSaving}
              className="w-full py-3 rounded-lg font-semibold text-white transition-all bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {termsSaving ? 'Saving...' : 'I Agree - Continue to SurFox AI'}
            </button>
          </div>
        </div>
      )}

      {/* Blocking Paused Subscription Modal */}
      {showPausedModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-900 border border-white/10 rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-8 text-center">
            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-amber-500/10">
              <AlertTriangle className="h-7 w-7 text-amber-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Your Account Is Paused</h2>
            <p className="text-white/70 text-sm mb-8 leading-relaxed">
              Your SurFox AI account is currently paused. To continue using the platform and access all of your data, please reach out to us and we'll get you back up and running.
            </p>
            <a
              href="mailto:admin@surfox.ai"
              className="inline-block w-full py-3 rounded-lg font-semibold text-white transition-all bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
            >
              Contact Us
            </a>
          </div>
        </div>
      )}

      {/* Mobile backdrop */}
      {mobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
<aside 
  className={`
    tour-sidebar
    ${(collapsed && !mobileOpen) ? 'w-[72px]' : 'w-56 lg:w-64'}
    ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
    fixed inset-y-0 left-0 z-50
    bg-slate-900
    transition-all duration-300 ease-out
    shadow-xl lg:shadow-none
    max-w-full
    flex flex-col
  `}
  onMouseEnter={() => !mobileOpen && setCollapsed(false)}
  onMouseLeave={() => !mobileOpen && setCollapsed(true)}
>
        {/* Header */}
<div className={`flex items-center ${(collapsed && !mobileOpen) ? 'justify-center' : 'justify-between'} px-4 h-16 border-b border-slate-800`}>
  <Link to="/dashboard" className="flex items-center space-x-3">
<img src="/logo.png" alt="SurFox AI" className={`${collapsed ? 'w-9 h-9' : 'w-8 h-8'} object-contain transition-all duration-200`} />
{(!collapsed || mobileOpen) && (
  <span className="text-white font-semibold text-lg tracking-tight whitespace-nowrap overflow-hidden">
    SurFox <span className="text-cyan-400">AI</span>
  </span>
)}
  </Link>
  <button 
    onClick={() => setMobileOpen(false)}
    className="lg:hidden p-1.5 rounded-lg hover:bg-slate-800 transition-colors text-slate-400"
  >
    <X size={18} />
  </button>
</div>

        {/* Navigation */}
<nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
  {navItems.map(({ path, label, icon: Icon, tourClass }) => {
    const isActive = location.pathname === path;
    return (
      <Link
        key={path}
        to={path}
        onClick={() => setMobileOpen(false)}
        title={collapsed ? label : undefined}
        className={`
          ${tourClass || ''}
          group flex items-center ${(collapsed && !mobileOpen) ? 'justify-center' : ''} px-3 py-2.5
          text-sm font-medium rounded-lg transition-all duration-150
          ${isActive 
            ? 'bg-slate-800 text-white' 
            : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
          }
        `}
      >
        <div className="relative flex-shrink-0">
          <Icon className={`w-5 h-5 ${(collapsed && !mobileOpen) ? '' : 'mr-3'} ${isActive ? 'text-indigo-400' : 'text-slate-500 group-hover:text-slate-300'}`} />
          {path === '/web-chat' && webChatUnread > 0 && (collapsed && !mobileOpen) && (
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-indigo-400 rounded-full" />
          )}
          {path === '/escalations' && escalationCount > 0 && (collapsed && !mobileOpen) && (
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
          )}
        </div>
        {(!collapsed || mobileOpen) && (
          <span className="whitespace-nowrap overflow-hidden flex-1">{label}</span>
        )}
        {(!collapsed || mobileOpen) && path === '/web-chat' && webChatUnread > 0 && (
          <span className="ml-auto bg-indigo-500 text-white text-xs font-medium px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
            {webChatUnread > 99 ? '99+' : webChatUnread}
          </span>
        )}
        {(!collapsed || mobileOpen) && path === '/escalations' && escalationCount > 0 && (
          <span className="ml-auto bg-red-500 text-white text-xs font-medium px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
            {escalationCount > 99 ? '99+' : escalationCount}
          </span>
        )}
        {(!collapsed || mobileOpen) && isActive && path !== '/web-chat' && (
          <div className="ml-auto w-1.5 h-1.5 bg-indigo-400 rounded-full" />
        )}
      </Link>
    );
  })}
                    {/* ADD THE PLATFORM UPDATES BUTTON HERE */}
          {/* Divider */}
<div className="my-4 border-t border-slate-800" />

{role === 'global_admin' && (
  <button
    onClick={() => setShowUpdatesModal(true)}
    title={collapsed ? 'Platform Updates' : undefined}
    className={`
      w-full group flex items-center ${(collapsed && !mobileOpen) ? 'justify-center' : ''} px-3 py-2.5
      text-sm font-medium rounded-lg transition-all duration-150
      text-slate-400 hover:bg-slate-800/50 hover:text-white
    `}
  >
    <Megaphone className={`w-5 h-5 flex-shrink-0 ${(collapsed && !mobileOpen) ? '' : 'mr-3'} text-slate-500 group-hover:text-slate-300`} />
    {(!collapsed || mobileOpen) && <span className="whitespace-nowrap overflow-hidden">Platform Updates</span>}
  </button>
)}

{role === 'global_admin' && (
  <Link
    to="/security-audit"
    onClick={() => setMobileOpen(false)}
    title={collapsed ? 'Security Audit' : undefined}
    className={`
      w-full group flex items-center ${(collapsed && !mobileOpen) ? 'justify-center' : ''} px-3 py-2.5
      text-sm font-medium rounded-lg transition-all duration-150
      ${location.pathname === '/security-audit'
        ? 'bg-slate-800 text-white'
        : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
      }
    `}
  >
    <Shield className={`w-5 h-5 flex-shrink-0 ${(collapsed && !mobileOpen) ? '' : 'mr-3'} ${
      location.pathname === '/security-audit' ? 'text-indigo-400' : 'text-slate-500 group-hover:text-slate-300'
    }`} />
    {(!collapsed || mobileOpen) && <span className="whitespace-nowrap overflow-hidden">Security Audit</span>}
    {(!collapsed || mobileOpen) && location.pathname === '/security-audit' && (
      <div className="ml-auto w-1.5 h-1.5 bg-indigo-400 rounded-full" />
    )}
  </Link>
)}

{role === 'global_admin' && (
  <Link
    to="/admin/affiliate-audit"
    onClick={() => setMobileOpen(false)}
    title={collapsed ? 'Affiliate Sign-Ins' : undefined}
    className={`
      w-full group flex items-center ${(collapsed && !mobileOpen) ? 'justify-center' : ''} px-3 py-2.5
      text-sm font-medium rounded-lg transition-all duration-150
      ${location.pathname === '/admin/affiliate-audit'
        ? 'bg-slate-800 text-white'
        : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
      }
    `}
  >
    <LogIn className={`w-5 h-5 flex-shrink-0 ${(collapsed && !mobileOpen) ? '' : 'mr-3'} ${
      location.pathname === '/admin/affiliate-audit' ? 'text-violet-400' : 'text-slate-500 group-hover:text-slate-300'
    }`} />
    {(!collapsed || mobileOpen) && <span className="whitespace-nowrap overflow-hidden">Affiliate Sign-Ins</span>}
    {(!collapsed || mobileOpen) && location.pathname === '/admin/affiliate-audit' && (
      <div className="ml-auto w-1.5 h-1.5 bg-violet-400 rounded-full" />
    )}
  </Link>
)}

{role === 'global_admin' && (
  <Link
    to="/admin/financials"
    onClick={() => setMobileOpen(false)}
    title={collapsed ? 'Financials' : undefined}
    className={`
      w-full group flex items-center ${(collapsed && !mobileOpen) ? 'justify-center' : ''} px-3 py-2.5
      text-sm font-medium rounded-lg transition-all duration-150
      ${location.pathname.startsWith('/admin/financials')
        ? 'bg-slate-800 text-white'
        : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
      }
    `}
  >
    <DollarSign className={`w-5 h-5 flex-shrink-0 ${(collapsed && !mobileOpen) ? '' : 'mr-3'} ${
      location.pathname.startsWith('/admin/financials') ? 'text-green-400' : 'text-slate-500 group-hover:text-slate-300'
    }`} />
    {(!collapsed || mobileOpen) && <span className="whitespace-nowrap overflow-hidden">Financials</span>}
    {(!collapsed || mobileOpen) && location.pathname.startsWith('/admin/financials') && (
      <div className="ml-auto w-1.5 h-1.5 bg-green-400 rounded-full" />
    )}
  </Link>
)}

{role === 'global_admin' && (
  <Link
    to="/admin/a2p"
    onClick={() => setMobileOpen(false)}
    title={collapsed ? 'A2P Status' : undefined}
    className={`
      w-full group flex items-center ${(collapsed && !mobileOpen) ? 'justify-center' : ''} px-3 py-2.5
      text-sm font-medium rounded-lg transition-all duration-150
      ${location.pathname === '/admin/a2p'
        ? 'bg-slate-800 text-white'
        : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
      }
    `}
  >
    <CheckSquare className={`w-5 h-5 flex-shrink-0 ${(collapsed && !mobileOpen) ? '' : 'mr-3'} ${
      location.pathname === '/admin/a2p' ? 'text-indigo-400' : 'text-slate-500 group-hover:text-slate-300'
    }`} />
    {(!collapsed || mobileOpen) && <span className="whitespace-nowrap overflow-hidden">A2P Status</span>}
    {(!collapsed || mobileOpen) && location.pathname === '/admin/a2p' && (
      <div className="ml-auto w-1.5 h-1.5 bg-indigo-400 rounded-full" />
    )}
  </Link>
)}

{role === 'global_admin' && (
  <Link
    to="/admin/customer-health"
    onClick={() => setMobileOpen(false)}
    title={collapsed ? 'Customer Health' : undefined}
    className={`
      w-full group flex items-center ${(collapsed && !mobileOpen) ? 'justify-center' : ''} px-3 py-2.5
      text-sm font-medium rounded-lg transition-all duration-150
      ${location.pathname === '/admin/customer-health'
        ? 'bg-slate-800 text-white'
        : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
      }
    `}
  >
    <HeartPulse className={`w-5 h-5 flex-shrink-0 ${(collapsed && !mobileOpen) ? '' : 'mr-3'} ${
      location.pathname === '/admin/customer-health' ? 'text-indigo-400' : 'text-slate-500 group-hover:text-slate-300'
    }`} />
    {(!collapsed || mobileOpen) && <span className="whitespace-nowrap overflow-hidden">Customer Health</span>}
    {(!collapsed || mobileOpen) && location.pathname === '/admin/customer-health' && (
      <div className="ml-auto w-1.5 h-1.5 bg-indigo-400 rounded-full" />
    )}
  </Link>
)}

{role === 'global_admin' && (
  <Link
    to="/admin/cron-health"
    onClick={() => setMobileOpen(false)}
    title={collapsed ? 'Cron Health' : undefined}
    className={`
      w-full group flex items-center ${(collapsed && !mobileOpen) ? 'justify-center' : ''} px-3 py-2.5
      text-sm font-medium rounded-lg transition-all duration-150
      ${location.pathname === '/admin/cron-health'
        ? 'bg-slate-800 text-white'
        : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
      }
    `}
  >
    <Timer className={`w-5 h-5 flex-shrink-0 ${(collapsed && !mobileOpen) ? '' : 'mr-3'} ${
      location.pathname === '/admin/cron-health' ? 'text-indigo-400' : 'text-slate-500 group-hover:text-slate-300'
    }`} />
    {(!collapsed || mobileOpen) && <span className="whitespace-nowrap overflow-hidden">Cron Health</span>}
    {(!collapsed || mobileOpen) && location.pathname === '/admin/cron-health' && (
      <div className="ml-auto w-1.5 h-1.5 bg-indigo-400 rounded-full" />
    )}
  </Link>
)}

{role === 'global_admin' && (
  <Link
    to="/admin/hunter"
    onClick={() => setMobileOpen(false)}
    title={collapsed ? 'Hunter' : undefined}
    className={`
      relative w-full group flex items-center ${(collapsed && !mobileOpen) ? 'justify-center' : ''} px-3 py-2.5
      text-sm font-medium rounded-lg transition-all duration-150
      ${location.pathname === '/admin/hunter'
        ? 'bg-slate-800 text-white'
        : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
      }
    `}
  >
    <HunterNavIcon className={`w-5 h-5 flex-shrink-0 ${(collapsed && !mobileOpen) ? '' : 'mr-3'} ${
      location.pathname === '/admin/hunter' ? 'text-indigo-400' : 'text-slate-500 group-hover:text-slate-300'
    }`} />
    {(!collapsed || mobileOpen) && <span className="whitespace-nowrap overflow-hidden">Hunter</span>}
    {hunterCount > 0 && (collapsed && !mobileOpen) && (
      <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
    )}
    {(!collapsed || mobileOpen) && hunterCount > 0 && (
      <span className="ml-auto bg-red-500 text-white text-xs font-medium px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
        {hunterCount > 99 ? '99+' : hunterCount}
      </span>
    )}
  </Link>
)}

{role === 'global_admin' && (
  <Link
    to="/qc-queue"
    onClick={() => setMobileOpen(false)}
    title={collapsed ? 'QC Queue' : undefined}
    className={`
      w-full group flex items-center ${(collapsed && !mobileOpen) ? 'justify-center' : ''} px-3 py-2.5
      text-sm font-medium rounded-lg transition-all duration-150
      ${location.pathname === '/qc-queue'
        ? 'bg-slate-800 text-white'
        : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
      }
    `}
  >
    <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${(collapsed && !mobileOpen) ? '' : 'mr-3'} ${
      location.pathname === '/qc-queue' ? 'text-indigo-400' : 'text-slate-500 group-hover:text-slate-300'
    }`} />
    {(!collapsed || mobileOpen) && <span className="whitespace-nowrap overflow-hidden">QC Queue</span>}
    {(!collapsed || mobileOpen) && location.pathname === '/qc-queue' && (
      <div className="ml-auto w-1.5 h-1.5 bg-indigo-400 rounded-full" />
    )}
  </Link>
)}

{role === 'global_admin' && (
  <Link
    to="/monthly-reports"
    onClick={() => setMobileOpen(false)}
    title={collapsed ? 'Monthly Reports' : undefined}
    className={`
      w-full group flex items-center ${(collapsed && !mobileOpen) ? 'justify-center' : ''} px-3 py-2.5
      text-sm font-medium rounded-lg transition-all duration-150
      ${location.pathname === '/monthly-reports'
        ? 'bg-slate-800 text-white'
        : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
      }
    `}
  >
    <FileText className={`w-5 h-5 flex-shrink-0 ${(collapsed && !mobileOpen) ? '' : 'mr-3'} ${
      location.pathname === '/monthly-reports' ? 'text-indigo-400' : 'text-slate-500 group-hover:text-slate-300'
    }`} />
    {(!collapsed || mobileOpen) && <span className="whitespace-nowrap overflow-hidden">Monthly Reports</span>}
    {(!collapsed || mobileOpen) && location.pathname === '/monthly-reports' && (
      <div className="ml-auto w-1.5 h-1.5 bg-indigo-400 rounded-full" />
    )}
  </Link>
)}

{role === 'global_admin' && (
  <Link
    to="/admin/email-manager"
    onClick={() => setMobileOpen(false)}
    title={collapsed ? 'Email Manager' : undefined}
    className={`
      w-full group flex items-center ${(collapsed && !mobileOpen) ? 'justify-center' : ''} px-3 py-2.5
      text-sm font-medium rounded-lg transition-all duration-150
      ${location.pathname === '/admin/email-manager'
        ? 'bg-slate-800 text-white'
        : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
      }
    `}
  >
    <Mail className={`w-5 h-5 flex-shrink-0 ${(collapsed && !mobileOpen) ? '' : 'mr-3'} ${
      location.pathname === '/admin/email-manager' ? 'text-indigo-400' : 'text-slate-500 group-hover:text-slate-300'
    }`} />
    {(!collapsed || mobileOpen) && <span className="whitespace-nowrap overflow-hidden">Email Manager</span>}
    {(!collapsed || mobileOpen) && location.pathname === '/admin/email-manager' && (
      <div className="ml-auto w-1.5 h-1.5 bg-indigo-400 rounded-full" />
    )}
  </Link>
)}

{role === 'global_admin' && (
  <Link
    to="/admin/tos-alerts"
    onClick={() => setMobileOpen(false)}
    title={collapsed ? 'TOS Alerts' : undefined}
    className={`
      w-full group flex items-center ${(collapsed && !mobileOpen) ? 'justify-center' : ''} px-3 py-2.5
      text-sm font-medium rounded-lg transition-all duration-150
      ${location.pathname === '/admin/tos-alerts'
        ? 'bg-slate-800 text-white'
        : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
      }
    `}
  >
    <ScrollText className={`w-5 h-5 flex-shrink-0 ${(collapsed && !mobileOpen) ? '' : 'mr-3'} ${
      location.pathname === '/admin/tos-alerts' ? 'text-indigo-400' : 'text-slate-500 group-hover:text-slate-300'
    }`} />
    {(!collapsed || mobileOpen) && <span className="whitespace-nowrap overflow-hidden">TOS Alerts</span>}
    {(!collapsed || mobileOpen) && location.pathname === '/admin/tos-alerts' && (
      <div className="ml-auto w-1.5 h-1.5 bg-indigo-400 rounded-full" />
    )}
  </Link>
)}

{role === 'global_admin' && (
  <Link
    to="/admin/voice-trunk-provisioning"
    onClick={() => setMobileOpen(false)}
    title={collapsed ? 'Voice Trunk Provisioning' : undefined}
    className={`
      w-full group flex items-center ${(collapsed && !mobileOpen) ? 'justify-center' : ''} px-3 py-2.5
      text-sm font-medium rounded-lg transition-all duration-150
      ${location.pathname === '/admin/voice-trunk-provisioning'
        ? 'bg-slate-800 text-white'
        : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
      }
    `}
  >
    <Phone className={`w-5 h-5 flex-shrink-0 ${(collapsed && !mobileOpen) ? '' : 'mr-3'} ${
      location.pathname === '/admin/voice-trunk-provisioning' ? 'text-indigo-400' : 'text-slate-500 group-hover:text-slate-300'
    }`} />
    {(!collapsed || mobileOpen) && <span className="whitespace-nowrap overflow-hidden">Voice Trunk Provisioning</span>}
    {(!collapsed || mobileOpen) && location.pathname === '/admin/voice-trunk-provisioning' && (
      <div className="ml-auto w-1.5 h-1.5 bg-indigo-400 rounded-full" />
    )}
  </Link>
)}

{role === 'global_admin' && (
  <Link
    to="/admin/reviewers"
    onClick={() => setMobileOpen(false)}
    title={collapsed ? 'Review Accounts' : undefined}
    className={`
      w-full group flex items-center ${(collapsed && !mobileOpen) ? 'justify-center' : ''} px-3 py-2.5
      text-sm font-medium rounded-lg transition-all duration-150
      ${location.pathname === '/admin/reviewers'
        ? 'bg-slate-800 text-white'
        : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
      }
    `}
  >
    <Shield className={`w-5 h-5 flex-shrink-0 ${(collapsed && !mobileOpen) ? '' : 'mr-3'} ${
      location.pathname === '/admin/reviewers' ? 'text-indigo-400' : 'text-slate-500 group-hover:text-slate-300'
    }`} />
    {(!collapsed || mobileOpen) && <span className="whitespace-nowrap overflow-hidden">Review Accounts</span>}
    {(!collapsed || mobileOpen) && location.pathname === '/admin/reviewers' && (
      <div className="ml-auto w-1.5 h-1.5 bg-indigo-400 rounded-full" />
    )}
  </Link>
)}

          {/* Calendar Settings - Available to all users */}
<Link
  to="/calendar-settings"
  title={collapsed ? 'Availability' : undefined}
  className={`
    group flex items-center ${(collapsed && !mobileOpen) ? 'justify-center' : ''} px-3 py-2.5
    text-sm font-medium rounded-lg transition-all duration-150
    ${location.pathname === '/calendar-settings'
      ? 'bg-slate-800 text-white' 
      : 'text-slate-400 hover:bg-slate-800/50 hover:text-white'
    }
  `}
>
  <Calendar className={`w-5 h-5 flex-shrink-0 ${(collapsed && !mobileOpen) ? '' : 'mr-3'} ${
    location.pathname === '/calendar-settings' ? 'text-indigo-400' : 'text-slate-500 group-hover:text-slate-300'
  }`} />
  {(!collapsed || mobileOpen) && <span className="whitespace-nowrap overflow-hidden">Availability</span>}
  {(!collapsed || mobileOpen) && location.pathname === '/calendar-settings' && (
    <div className="ml-auto w-1.5 h-1.5 bg-indigo-400 rounded-full" />
  )}
</Link>
          
        </nav>

        {/* Sidebar Footer - Surf, Tour & Updates */}
        <div className="p-3 border-t border-slate-800 space-y-2">
          {/* Surf — summons the agent panel. A wordmark, not an icon row: the rail
              header is already a fox (logo.png), so a second fox down here would read
              as the brand twice over. The word renders identically at 72px collapsed
              and expanded, so it needs no collapse handling.
              Fires the same 'surf:open' event the Campaigns CTA uses rather than
              mounting SurfWidget here — the <aside> carries a translate class, which
              would make it the containing block for the panel's `fixed` and pin the
              panel to the 72px rail instead of the viewport. */}
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('surf:open'))}
            className={`
              w-full flex items-center ${(collapsed && !mobileOpen) ? 'justify-center' : ''} px-3 py-2.5
              text-sm font-semibold tracking-tight rounded-lg transition-all duration-150
              text-cyan-400 hover:bg-slate-800/50 hover:text-cyan-300
            `}
          >
            Surf
          </button>
          <NotificationCenter collapsed={collapsed} mobileOpen={mobileOpen} inSidebar />
        </div>
      </aside>

      {/* Main content */}
      <div className={`flex flex-col flex-1 min-h-screen ${collapsed ? 'lg:ml-[72px]' : 'lg:ml-64'} w-full max-w-full overflow-x-hidden transition-all duration-300 relative z-30`}>
       <GetLiveProvider>
        {/* Top header */}
        <header className="bg-white border-b border-gray-200 px-4 lg:px-6 py-3 h-16">
          <div className="flex justify-between items-center h-full w-full min-w-0">
            <div className="flex items-center space-x-4 min-w-0 flex-1">
  <button
    onClick={() => setMobileOpen(true)}
    className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-600"
  >
    <Menu size={20} />
  </button>
  
  <h1 className="hidden lg:block text-lg font-medium text-gray-900">
    {getPageTitle()}
  </h1>
</div>
            
            <div className="flex items-center space-x-2 lg:space-x-3 flex-shrink-0 ml-2">
              {/* Search Box */}
              {/* Modern Global Search Trigger */}
              <button
                onClick={() => setIsGlobalSearchOpen(true)}
                className="tour-global-search hidden md:flex items-center gap-2 px-3 py-2 bg-slate-50 hover:bg-slate-100
                           border border-slate-200 rounded-xl text-sm text-slate-600 hover:text-slate-900
                           transition-colors group"
              >
                <Search className="w-4 h-4 text-slate-400 group-hover:text-slate-600 transition-colors" />
                <span>Search leads, campaigns...</span>
                <kbd className="hidden lg:inline-flex items-center gap-0.5 px-2 py-0.5 text-xs font-medium bg-white border border-slate-200 rounded">
                  <span>⌘</span>
                  <span>/</span>
                </kbd>
              </button>
              
              {/* Mobile Search Button */}
              <button 
                onClick={() => setShowMobileSearch(true)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-600"
              >
                <Search size={18} />
              </button>
              
              {/* Notifications - UPDATED */}
              <div className="relative notifications-container">
                <button 
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-600"
                >
                  <Bell size={18} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </button>
                
                {/* Notifications Dropdown */}
                {showNotifications && (
                  <div className="fixed top-16 right-2 left-2 sm:left-auto sm:right-4 sm:w-[380px] max-h-[70vh] sm:max-h-[560px] bg-white rounded-xl border border-gray-200 shadow-2xl z-[200] flex flex-col overflow-hidden">
                    {/* Header */}
                    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900 text-sm">Notifications</h3>
                        {unreadCount > 0 && (
                          <span className="min-w-[20px] h-5 bg-indigo-600 text-white text-xs font-bold rounded-full flex items-center justify-center px-1.5">
                            {unreadCount}
                          </span>
                        )}
                      </div>
                      {unreadCount > 0 && (
                        <button
                          onClick={markAllAsRead}
                          className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
                        >
                          <Check className="w-3 h-3" />
                          Mark all read
                        </button>
                      )}
                    </div>

                    {/* Notifications List */}
                    <div className="flex-1 overflow-y-auto">
                      {notificationsLoading ? (
                        <div className="py-12 text-center">
                          <div className="inline-flex items-center text-sm text-gray-400">
                            <svg className="animate-spin mr-2 h-4 w-4 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading...
                          </div>
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="py-12 px-4 text-center">
                          <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                            <Bell className="w-5 h-5 text-gray-400" />
                          </div>
                          <p className="text-sm font-medium text-gray-900">All caught up</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {role === 'business_admin' || role === 'global_admin'
                              ? 'No escalated leads right now'
                              : 'No notifications for your campaigns'}
                          </p>
                        </div>
                      ) : (
                        (() => {
                          const { individual, batched } = organizeNotifications(notifications);
                          const allNotifications = [...individual, ...batched];

                          const priorityConfig = {
                            critical: { border: 'border-l-red-500',   avatar: 'bg-red-100 text-red-700',   dot: 'bg-red-500',   label: 'Critical' },
                            high:     { border: 'border-l-amber-400', avatar: 'bg-amber-100 text-amber-700', dot: 'bg-amber-400', label: 'High'     },
                            medium:   { border: 'border-l-blue-500',  avatar: 'bg-blue-100 text-blue-700',  dot: 'bg-blue-500',  label: 'Medium'   },
                            low:      { border: 'border-l-gray-300',  avatar: 'bg-gray-100 text-gray-600',  dot: 'bg-gray-400',  label: 'Low'      },
                          };

                          const getRelativeTime = (ts) => {
                            const diff = Date.now() - new Date(ts).getTime();
                            const m = Math.floor(diff / 60000);
                            const h = Math.floor(m / 60);
                            const d = Math.floor(h / 24);
                            if (m < 1)  return 'just now';
                            if (m < 60) return `${m}m ago`;
                            if (h < 24) return `${h}h ago`;
                            if (d < 7)  return `${d}d ago`;
                            return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                          };

                          return allNotifications.map((notification) => {
                            const cfg = priorityConfig[notification.priority] || priorityConfig.low;
                            const isBatch = notification.type === 'batch';
                            const leadName = notification.leads?.name || 'Unknown Lead';
                            const initials = leadName.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();
                            const campaignName = notification.leads?.campaigns?.name;

                            if (isBatch) {
                              return (
                                <button
                                  key={notification.id}
                                  onClick={() => navigate('/dashboard')}
                                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-left border-b border-gray-50"
                                >
                                  <div className={`w-8 h-8 rounded-full ${cfg.avatar} flex items-center justify-center text-xs font-bold flex-shrink-0`}>
                                    {notification.count}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-800">{notification.title}</p>
                                    <p className="text-xs text-gray-500 truncate mt-0.5">{notification.message}</p>
                                  </div>
                                  <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                </button>
                              );
                            }

                            return (
                              <div
                                key={notification.id}
                                className={`group relative border-l-4 ${cfg.border} border-b border-gray-50 ${!notification.read ? 'bg-white' : 'bg-gray-50/50'} transition-colors hover:bg-indigo-50/20`}
                              >
                                <button
                                  onClick={() => handleNotificationClick(notification)}
                                  className="w-full flex items-start gap-3 px-4 py-3.5 text-left pr-16"
                                >
                                  {/* Lead avatar */}
                                  <div className={`w-8 h-8 rounded-full ${cfg.avatar} flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5`}>
                                    {initials}
                                  </div>

                                  {/* Content */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 mb-0.5">
                                      <span className={`text-sm ${!notification.read ? 'font-semibold text-gray-900' : 'font-medium text-gray-600'} truncate`}>
                                        {leadName}
                                      </span>
                                      {!notification.read && (
                                        <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
                                      )}
                                    </div>
                                    <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                                      {stripScoreFromMessage(notification.message)}
                                    </p>
                                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                      {campaignName && (
                                        <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded truncate max-w-[160px]">
                                          {campaignName}
                                        </span>
                                      )}
                                      <span className="text-xs text-gray-400">{getRelativeTime(notification.created_at)}</span>
                                    </div>
                                  </div>
                                </button>

                                {/* Hover action buttons */}
                                <div className="absolute right-2 top-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  {!notification.read && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); markAsRead(notification.id); }}
                                      className="p-1.5 rounded-md bg-white border border-gray-200 hover:bg-green-50 hover:border-green-300 transition-colors shadow-sm"
                                      title="Mark as read"
                                    >
                                      <Check className="w-3 h-3 text-gray-400 hover:text-green-600" />
                                    </button>
                                  )}
                                  <button
                                    onClick={(e) => dismissNotification(e, notification.id)}
                                    className="p-1.5 rounded-md bg-white border border-gray-200 hover:bg-red-50 hover:border-red-300 transition-colors shadow-sm"
                                    title="Dismiss"
                                  >
                                    <X className="w-3 h-3 text-gray-400 hover:text-red-500" />
                                  </button>
                                </div>
                              </div>
                            );
                          });
                        })()
                      )}
                    </div>

                    {/* Footer */}
                    {notifications.length > 0 && (
                      <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50/60">
                        <button
                          onClick={() => {
                            setShowNotifications(false);
                            setShowNotificationsModal(true);
                          }}
                          className="w-full text-center text-xs font-medium text-indigo-600 hover:text-indigo-800 transition-colors"
                        >
                          View all notifications →
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
          {/* Settings Gear */}
          <Link 
            to="/settings" 
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-600 tour-settings"
            title="Settings"
          >
            <Settings size={18} />
          </Link>

          {/* Help */}
          <Link to="/help" className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-600">
            <HelpCircle size={18} />
          </Link>
              
              {/* User menu */}
              <div className="relative user-menu-container">
                <button
  onClick={() => setShowTopUserMenu(!showTopUserMenu)}
  className="flex items-center space-x-1 p-2 rounded-lg hover:bg-gray-100 transition-all duration-200"
>
  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
    <User className="w-5 h-5 text-gray-500" />
  </div>
  <ChevronDown className="w-4 h-4 text-gray-400" />
</button>
                
                {/* Dropdown */}
                {showTopUserMenu && (
  <div className="fixed top-14 right-2 w-[calc(100vw-1rem)] sm:w-56 sm:right-4 bg-white rounded-lg border border-gray-200 shadow-xl z-[9999] max-w-[224px]">
                    <div className="p-3 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{companyInfo.name}</p>
                    </div>
                    <div className="py-1">
                      <Link to="/billing" className="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                        <CreditCard className="w-4 h-4 mr-3 text-gray-400" />
                        Billing & Subscription
                      </Link>
                      <Link to="/referrals" className="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      <Users className="w-4 h-4 mr-3 text-gray-400" />
                      Referrals
                    </Link>
                  {/* Affiliates - Global Admin Only */}
                  {role === 'global_admin' && (
                    <Link to="/admin/affiliates" className="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      <DollarSign className="w-4 h-4 mr-3 text-gray-400" />
                      Affiliates
                    </Link>
                  )}
                  {/* Partner Invites - Global Admin Only */}
                  {role === 'global_admin' && (
                    <Link to="/partner-invite" className="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                      <Users className="w-4 h-4 mr-3 text-gray-400" />
                      Partner Invites
                    </Link>
                  )}
                      <Link to="/settings" className="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                        <Settings className="w-4 h-4 mr-3 text-gray-400" />
                        Settings & Preferences
                      </Link>
                      <Link to="/help" className="flex items-center px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                        <HelpCircle className="w-4 h-4 mr-3 text-gray-400" />
                        Help & Support
                      </Link>
                    </div>
                    <div className="border-t border-gray-100 py-1">
                      <button 
                        onClick={handleLogout}
                        className="w-full flex items-center px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <LogOut className="w-4 h-4 mr-3" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* A2P trial banner — in-flow under the topbar (Get-Live activation). */}
        {/* Review sandbox: no A2P by design, so its "finish your A2P setup" nag would say the account is unfinished. */}
        {!user?.is_reviewer && <A2PTrialBanner />}

        {/* Mobile Search Overlay */}
        {showMobileSearch && (
          <div className="fixed inset-0 z-50 md:hidden">
            <div className="bg-white h-full flex flex-col">
              {/* Search Header */}
              <div className="flex items-center p-4 border-b border-gray-200">
                <button
                  onClick={() => setShowMobileSearch(false)}
                  className="p-2 -ml-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-600 mr-3"
                >
                  <X size={20} />
                </button>
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search leads, campaigns..."
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-base focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    autoFocus
                  />
                </div>
              </div>
            
              {/* Mobile Search Results */}
              <div className="flex-1 overflow-y-auto">
                {isSearching ? (
                  <div className="p-8 text-center text-sm text-gray-500">
                    <div className="inline-flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Searching...
                    </div>
                  </div>
                ) : searchResults && (searchResults.leads.length > 0 || searchResults.campaigns.length > 0) ? (
                  <div>
                    {/* Mobile Leads Section */}
                    {searchResults.leads.length > 0 && (
                      <div>
                        <div className="px-4 py-3 text-xs font-semibold text-gray-500 bg-gray-50 border-b border-gray-100">
                          LEADS
                        </div>
                        {searchResults.leads.map((lead) => (
                          <button
                            key={`mobile-lead-${lead.id}`}
                            onClick={() => {
                              handleSearchResultClick('lead', lead);
                              setShowMobileSearch(false);
                            }}
                            className="w-full px-4 py-4 text-left hover:bg-gray-50 transition-colors border-b border-gray-100"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="text-base font-medium text-gray-900 truncate">
                                  {lead.name || 'Unnamed Lead'}
                                </div>
                                <div className="text-sm text-gray-500 mt-1 truncate">
                                  {lead.email || lead.phone || 'No contact info'}
                                </div>
                                {lead.ai_score && (
                                  <div className="text-xs text-gray-600 mt-1">
                                    Score: {lead.ai_score}
                                  </div>
                                )}
                              </div>
                              <div className="ml-3 flex-shrink-0">
                                <span className={`text-xs px-2 py-1 rounded-full ${
                                  lead.status === 'active' ? 'bg-green-100 text-green-700' :
                                  lead.status === 'qualified' ? 'bg-blue-100 text-blue-700' :
                                  lead.status === 'disqualified' ? 'bg-red-100 text-red-700' :
                                  lead.status === 'contacted' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-gray-100 text-gray-700'
                                }`}>
                                  {lead.status || 'Unknown'}
                                </span>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                    
                    {/* Mobile Campaigns Section */}
                    {searchResults.campaigns.length > 0 && (
                      <div>
                        <div className="px-4 py-3 text-xs font-semibold text-gray-500 bg-gray-50 border-b border-gray-100">
                          CAMPAIGNS
                        </div>
                        {searchResults.campaigns.map((campaign) => (
                          <button
                            key={`mobile-campaign-${campaign.id}`}
                            onClick={() => {
                              handleSearchResultClick('campaign', campaign);
                              setShowMobileSearch(false);
                            }}
                            className="w-full px-4 py-4 text-left hover:bg-gray-50 transition-colors border-b border-gray-100"
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="text-base font-medium text-gray-900 truncate">
                                  {campaign.name}
                                </div>
                                <div className="text-sm text-gray-500 mt-1 line-clamp-2">
                                  {campaign.description || `Started: ${campaign.start_date ? new Date(campaign.start_date).toLocaleDateString() : 'No start date'}`}
                                </div>
                              </div>
                              <div className="ml-3 flex-shrink-0 flex flex-col items-end space-y-1">
                                {campaign.ai_on && (
                                  <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                                    AI
                                  </span>
                                )}
                                <span className={`text-xs px-2 py-1 rounded-full ${
                                  campaign.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                                }`}>
                                  {campaign.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : searchQuery.length >= 2 ? (
                  <div className="p-8 text-center">
                    <div className="text-gray-400 mb-3">
                      <Search size={48} className="mx-auto" />
                    </div>
                    <p className="text-base text-gray-500 mb-1">No results found for "{searchQuery}"</p>
                    <p className="text-sm text-gray-400">Try searching with different keywords</p>
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <div className="text-gray-400 mb-3">
                      <Search size={48} className="mx-auto" />
                    </div>
                    <p className="text-base text-gray-500 mb-1">Search your leads and campaigns</p>
                    <p className="text-sm text-gray-400">Type at least 2 characters to start searching</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

{/* A2P Status Notifications */}
        {a2pNotifications.length > 0 && (
          <div className="fixed top-20 right-4 z-50 space-y-2 max-w-md">
            {a2pNotifications.map(notification => (
              <div
                key={notification.id}
                className={`p-4 rounded-lg shadow-lg border animate-slideIn ${
                  notification.type === 'success'
                    ? 'bg-green-50 border-green-200'
                    : notification.type === 'error'
                    ? 'bg-red-50 border-red-200'
                    : 'bg-blue-50 border-blue-200'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-1">
                    <p className={`font-medium text-sm ${
                      notification.type === 'success' ? 'text-green-800' :
                      notification.type === 'error' ? 'text-red-800' :
                      'text-blue-800'
                    }`}>
                      {notification.message}
                    </p>
                  </div>
                  <button
                    onClick={() => setA2pNotifications(prev => prev.filter(n => n.id !== notification.id))}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Floating Chat Button - Lazy loads Crisp to avoid local network permission prompt */}
        {user?.email && !chatLoaded && (
          <button
            onClick={handleChatClick}
            disabled={chatLoading}
            className="fixed bottom-6 right-6 z-40 flex items-center justify-center w-14 h-14 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full shadow-lg transition-all duration-200 hover:scale-105 disabled:opacity-70 disabled:cursor-wait"
            title="Chat with Support"
          >
            {chatLoading ? (
              <svg className="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <MessageCircle className="w-6 h-6" />
            )}
          </button>
        )}

{/* Main content area */}
<main className={`flex-1 ${isControlRoom ? '' : 'p-4 lg:p-6'} bg-gray-50 w-full max-w-full overflow-x-hidden${user?.is_prospect_demo && !location.pathname.startsWith('/onboarding') ? ' lg:pr-[396px] pb-[46vh] lg:pb-0' : ''}`}>
  {/* Account paused by admin — messaging kill switch is off. The customer cannot
      re-enable this themselves; only a GlobalAdmin can restore messaging. */}
  <ReviewerSandboxBanner user={user} />
  <ReviewerIntroVideo />
  {user && user.messaging_enabled === false && !user.is_reviewer && (
    <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3">
      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="text-sm">
        <p className="font-semibold text-amber-900">Your account has been temporarily paused</p>
        <p className="text-amber-800 mt-0.5">
          Outbound messaging is currently disabled for your account. Please contact support
          to restore service.
        </p>
      </div>
    </div>
  )}
  <AdminNoticeBanner user={user} />
  <LeadInventoryBanner user={user} pathname={location.pathname} />
  <UsageLimitBanner user={user} pathname={location.pathname} />
  {children}
</main>

{/* In-platform Surf — floating fox button bottom-right, summons a side
    panel on click. Opened to all authed tenants 2026-07-02 after the write
    path (commit/clone/phone-buy) was verified working and tenant-isolated;
    new campaigns commit paused, tenant scope comes from auth not the model. */}
<SurfWidget />

{/* Get-Live Companion — persistent launcher + slide-up panel (replaces the old tour). */}
{/* Review sandbox: the "N things left to go live" checklist is onboarding, which a reviewer skips. */}
{!user?.is_reviewer && <GetLiveCompanion />}

{/* How-to video popup — shows when a checklist step navigates with ?howto=<key>. */}
<HowToVideoModal />

        {/* ADD THE MODAL HERE - RIGHT BEFORE PRODUCTTOUR */}
<PlatformUpdatesModal
  isOpen={showUpdatesModal}
  onClose={() => setShowUpdatesModal(false)}
/>

<NotificationsModal
  isOpen={showNotificationsModal}
  onClose={() => setShowNotificationsModal(false)}
  notifications={notifications}
  onMarkAsRead={markAsRead}
  onDismiss={dismissNotification}
  onMarkAllAsRead={markAllAsRead}
  role={role}
/>

{/* Global Search Palette */}
<GlobalSearchPalette
  isOpen={isGlobalSearchOpen}
  onClose={() => setIsGlobalSearchOpen(false)}
/>
       </GetLiveProvider>
      </div>
    </div>
  );
};